import type { ArticleBlock } from "@/lib/types/content";
import { hasCraftContent, parseCraftState, type CraftNodeMap } from "./serialize";
import { resolveHeadingId, slugifyHeading } from "@/lib/scroll-to-anchor";
import { extractDocHeadings } from "@/lib/doc/doc-headings";
import type { EditorDoc } from "@/lib/doc/doc-types";

export type PageHeading = {
  text: string;
  id: string;
  level: "h2" | "h3";
};

export { slugifyHeading, resolveHeadingId } from "@/lib/scroll-to-anchor";

function walkCraftNodes(state: CraftNodeMap, nodeId: string, headings: PageHeading[]) {
  const node = state[nodeId];
  if (!node) return;

  if (node.type?.resolvedName === "TextBlock") {
    const variant = node.props?.variant;
    const text = String(node.props?.text ?? "").trim();
    const anchorId = typeof node.props?.anchorId === "string" ? node.props.anchorId : undefined;
    if (text && (variant === "h2" || variant === "h3")) {
      headings.push({
        text,
        id: resolveHeadingId(text, anchorId),
        level: variant,
      });
    }
  }

  for (const childId of node.nodes ?? []) {
    walkCraftNodes(state, childId, headings);
  }
}

export function extractCraftHeadings(craftState: string | object | null | undefined): PageHeading[] {
  const state = parseCraftState(craftState);
  if (!state?.ROOT) return [];

  const headings: PageHeading[] = [];
  for (const childId of state.ROOT.nodes ?? []) {
    walkCraftNodes(state, childId, headings);
  }
  return headings;
}

export function extractBlockHeadings(body: ArticleBlock[]): PageHeading[] {
  return body
    .filter((block): block is { type: "h2"; text: string } => block.type === "h2")
    .map((block) => ({
      text: block.text,
      id: slugifyHeading(block.text) || "section",
      level: "h2" as const,
    }));
}

/** Headings for the “On this page” nav — prefers editor doc, then Craft, then legacy blocks. */
export function extractPageHeadings(options: {
  editorDoc?: unknown;
  craftState?: string | object | null;
  body?: ArticleBlock[];
  tocLevel?: "h2" | "h2-h3";
}): PageHeading[] {
  const { tocLevel = "h2" } = options;
  let headings: PageHeading[] = [];

  if (Array.isArray(options.editorDoc) && options.editorDoc.length > 0) {
    headings = extractDocHeadings(options.editorDoc as EditorDoc, tocLevel).map((h) => ({
      ...h,
      level: h.level === 2 ? ("h2" as const) : ("h3" as const),
    }));
  } else if (hasCraftContent(options.craftState)) {
    headings = extractCraftHeadings(options.craftState);
  } else if (options.body?.length) {
    headings = extractBlockHeadings(options.body);
  }

  if (tocLevel === "h2") {
    return headings.filter((h) => h.level === "h2");
  }
  return headings;
}
